//frontend/dispatcher-client/src/components/personnel/PersonnelPublicHolidayFormModal.jsx
import React, {
  useState,
} from "react";

import {
  LuCalendarDays,
  LuSave,
  LuTrash2,
} from "react-icons/lu";

import {
  useTranslation,
} from "react-i18next";

import {
  createPersonnelPublicHoliday,
  updatePersonnelPublicHoliday,
  deletePersonnelPublicHoliday,
} from "../../api/personnelApi";

import {
  markPersonnelResourceStale,
} from "../../utils/personnelCache";

/* =========================================================
 * HELPERS
 * ======================================================= */

const pad2 = (
  value
) =>
  String(
    value
  ).padStart(
    2,
    "0"
  );

const getDefaultDate =
  (
    year
  ) => {
    const now =
      new Date();

    if (
      now.getFullYear() ===
      year
    ) {
      return [
        year,
        pad2(
          now.getMonth() +
            1
        ),
        pad2(
          now.getDate()
        ),
      ].join("-");
    }

    return `${year}-01-01`;
  };

/* =========================================================
 * COMPONENT
 * ======================================================= */

export default function PersonnelPublicHolidayFormModal({
  year,
  holiday = null,
  canDelete = false,
  onClose,
  onChanged,
}) {
  const {
    t,
  } =
    useTranslation(
      "personnel"
    );

  const isEdit =
    !!holiday?.id;

  const [
    name,
    setName,
  ] =
    useState(
      holiday?.name ||
        ""
    );

  const [
    date,
    setDate,
  ] =
    useState(
      holiday?.date ||
        getDefaultDate(
          year
        )
    );

  const [
    countryCode,
    setCountryCode,
  ] =
    useState(
      holiday?.countryCode ||
        "PT"
    );

  const [
    regionCode,
    setRegionCode,
  ] =
    useState(
      holiday?.regionCode ||
        ""
    );

  const [
    isRecurring,
    setIsRecurring,
  ] =
    useState(
      holiday?.isRecurring ??
        false
    );

  const [
    isPaidHoliday,
    setIsPaidHoliday,
  ] =
    useState(
      holiday?.isPaidHoliday ??
        true
    );

  const [
    notes,
    setNotes,
  ] =
    useState(
      holiday?.notes ||
        ""
    );

  const [
    saving,
    setSaving,
  ] =
    useState(
      false
    );

  const [
    deleting,
    setDeleting,
  ] =
    useState(
      false
    );

  const [
    confirmDelete,
    setConfirmDelete,
  ] =
    useState(
      false
    );

  const [
    error,
    setError,
  ] =
    useState(
      null
    );

  const busy =
    saving ||
    deleting;

  /* =======================================================
   * PAYLOAD
   * ===================================================== */

  const buildPayload =
    () => {
      const cleanName =
        name.trim();

      const cleanCountry =
        countryCode
          .trim()
          .toUpperCase();

      const cleanRegion =
        regionCode
          .trim()
          .toUpperCase();

      if (!cleanName) {
        throw new Error(
          t(
            "public_holidays.form.errors.name_required"
          )
        );
      }

      if (!date) {
        throw new Error(
          t(
            "public_holidays.form.errors.date_required"
          )
        );
      }

      if (!cleanCountry) {
        throw new Error(
          t(
            "public_holidays.form.errors.country_required"
          )
        );
      }

      return {
        name:
          cleanName,

        date,

        countryCode:
          cleanCountry,

        regionCode:
          cleanRegion ||
          null,

        isRecurring,

        isPaidHoliday,

        notes:
          notes.trim() ||
          null,
      };
    };

  /* =======================================================
   * SAVE
   * ===================================================== */

  const handleSave =
    async () => {
      setError(
        null
      );

      setConfirmDelete(
        false
      );

      try {
        const payload =
          buildPayload();

        setSaving(
          true
        );

        let savedHoliday =
          null;

        if (isEdit) {
          savedHoliday =
            await updatePersonnelPublicHoliday(
              holiday.id,
              payload
            );
        } else {
          savedHoliday =
            await createPersonnelPublicHoliday(
              payload
            );
        }

        /*
         * Public Holidays page and Calendar use
         * year-scoped publicHoliday caches.
         *
         * Mark all of them stale so Calendar will
         * silently refresh next time it is opened.
         */
        markPersonnelResourceStale(
          "publicHolidays"
        );

        if (
          typeof onChanged ===
          "function"
        ) {
          await onChanged({
            action:
              isEdit
                ? "updated"
                : "created",

            holiday:
              savedHoliday ||
              payload,
          });
        }

        onClose();
      } catch (
        saveError
      ) {
        console.error(
          "[PersonnelPublicHolidayForm] save failed:",
          saveError
        );

        setError(
          saveError
            ?.response
            ?.data
            ?.error ||
          saveError
            ?.message ||
          t(
            "public_holidays.form.errors.save_failed"
          )
        );
      } finally {
        setSaving(
          false
        );
      }
    };

  /* =======================================================
   * DELETE
   * ===================================================== */

  const handleDelete =
    async () => {
      if (
        !canDelete ||
        !isEdit
      ) {
        return;
      }

      setError(
        null
      );

      try {
        setDeleting(
          true
        );

        await deletePersonnelPublicHoliday(
          holiday.id
        );

        markPersonnelResourceStale(
          "publicHolidays"
        );

        if (
          typeof onChanged ===
          "function"
        ) {
          await onChanged({
            action:
              "deleted",

            holiday,
          });
        }

        onClose();
      } catch (
        deleteError
      ) {
        console.error(
          "[PersonnelPublicHolidayForm] delete failed:",
          deleteError
        );

        const status =
          deleteError
            ?.response
            ?.status;

        const code =
          deleteError
            ?.response
            ?.data
            ?.code;

        if (
          status ===
            409 ||
          code ===
            "PUBLIC_HOLIDAY_IN_USE"
        ) {
          setError(
            t(
              "public_holidays.form.errors.in_use"
            )
          );

          return;
        }

        setError(
          deleteError
            ?.response
            ?.data
            ?.error ||
          deleteError
            ?.message ||
          t(
            "public_holidays.form.errors.delete_failed"
          )
        );
      } finally {
        setDeleting(
          false
        );
      }
    };

  /* =======================================================
   * RENDER
   * ===================================================== */

  return (
    <div
      className="personnel-holiday-form-overlay"
      onMouseDown={(
        event
      ) => {
        if (
          event.target ===
            event.currentTarget &&
          !busy
        ) {
          onClose();
        }
      }}
    >
      <div
        className="personnel-holiday-form-modal"
        role="dialog"
        aria-modal="true"
      >
        <div className="personnel-holiday-form-header">
          <div>
            <span>
              {isEdit
                ? t(
                    "public_holidays.form.edit_kicker"
                  )
                : t(
                    "public_holidays.form.create_kicker"
                  )}
            </span>

            <h3>
              {isEdit
                ? t(
                    "public_holidays.form.edit_title"
                  )
                : t(
                    "public_holidays.form.create_title"
                  )}
            </h3>
          </div>

          <button
            type="button"
            onClick={
              onClose
            }
            disabled={
              busy
            }
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="personnel-holiday-form-grid">
          <label className="personnel-holiday-form-field personnel-holiday-form-field--wide">
            <span>
              {t(
                "public_holidays.form.name"
              )}
            </span>

            <input
              type="text"
              value={
                name
              }
              onChange={(
                event
              ) =>
                setName(
                  event.target
                    .value
                )
              }
              placeholder={t(
                "public_holidays.form.name_placeholder"
              )}
              disabled={
                busy
              }
            />
          </label>

          <label className="personnel-holiday-form-field personnel-holiday-form-field--wide">
            <span>
              {t(
                "public_holidays.form.date"
              )}
            </span>

            <div className="personnel-holiday-form-input-with-icon">
              <LuCalendarDays />

              <input
                type="date"
                value={
                  date
                }
                onChange={(
                  event
                ) =>
                  setDate(
                    event.target
                      .value
                  )
                }
                disabled={
                  busy
                }
              />
            </div>
          </label>

          <label className="personnel-holiday-form-field">
            <span>
              {t(
                "public_holidays.form.country"
              )}
            </span>

            <input
              type="text"
              value={
                countryCode
              }
              maxLength={2}
              onChange={(
                event
              ) =>
                setCountryCode(
                  event.target
                    .value
                    .toUpperCase()
                )
              }
              placeholder="PT"
              disabled={
                busy
              }
            />
          </label>

          <label className="personnel-holiday-form-field">
            <span>
              {t(
                "public_holidays.form.region"
              )}
            </span>

            <input
              type="text"
              value={
                regionCode
              }
              onChange={(
                event
              ) =>
                setRegionCode(
                  event.target
                    .value
                )
              }
              placeholder={t(
                "public_holidays.form.region_placeholder"
              )}
              disabled={
                busy
              }
            />
          </label>

          <label className="personnel-holiday-form-toggle">
            <input
              type="checkbox"
              checked={
                isRecurring
              }
              onChange={(
                event
              ) =>
                setIsRecurring(
                  event.target
                    .checked
                )
              }
              disabled={
                busy
              }
            />

            <div>
              <strong>
                {t(
                  "public_holidays.form.recurring"
                )}
              </strong>

              <span>
                {t(
                  "public_holidays.form.recurring_help"
                )}
              </span>
            </div>
          </label>

          <label className="personnel-holiday-form-toggle">
            <input
              type="checkbox"
              checked={
                isPaidHoliday
              }
              onChange={(
                event
              ) =>
                setIsPaidHoliday(
                  event.target
                    .checked
                )
              }
              disabled={
                busy
              }
            />

            <div>
              <strong>
                {t(
                  "public_holidays.form.paid"
                )}
              </strong>

              <span>
                {t(
                  "public_holidays.form.paid_help"
                )}
              </span>
            </div>
          </label>

          <label className="personnel-holiday-form-field personnel-holiday-form-field--wide">
            <span>
              {t(
                "public_holidays.form.notes"
              )}
            </span>

            <textarea
              value={
                notes
              }
              onChange={(
                event
              ) =>
                setNotes(
                  event.target
                    .value
                )
              }
              rows={3}
              placeholder={t(
                "public_holidays.form.notes_placeholder"
              )}
              disabled={
                busy
              }
            />
          </label>
        </div>

        {error && (
          <div className="personnel-holiday-form-error">
            {
              error
            }
          </div>
        )}

        {confirmDelete ? (
          <div className="personnel-holiday-delete-confirm">
            <p>
              {t(
                "public_holidays.form.confirm_delete"
              )}
            </p>

            <div>
              <button
                type="button"
                className="personnel-request-action-btn is-secondary"
                onClick={() =>
                  setConfirmDelete(
                    false
                  )
                }
                disabled={
                  busy
                }
              >
                {t(
                  "public_holidays.form.back"
                )}
              </button>

              <button
                type="button"
                className="personnel-request-action-btn is-reject"
                onClick={
                  handleDelete
                }
                disabled={
                  busy
                }
              >
                {deleting
                  ? t(
                      "public_holidays.form.working"
                    )
                  : t(
                      "public_holidays.form.delete_confirm_button"
                    )}
              </button>
            </div>
          </div>
        ) : (
          <div className="personnel-holiday-form-actions">
            <div>
              {canDelete &&
                isEdit && (
                <button
                  type="button"
                  className="personnel-holiday-delete-btn"
                  onClick={() =>
                    setConfirmDelete(
                      true
                    )
                  }
                  disabled={
                    busy
                  }
                >
                  <LuTrash2 />

                  {t(
                    "public_holidays.form.delete"
                  )}
                </button>
              )}
            </div>

            <div>
              <button
                type="button"
                className="personnel-request-action-btn is-secondary"
                onClick={
                  onClose
                }
                disabled={
                  busy
                }
              >
                {t(
                  "public_holidays.form.close"
                )}
              </button>

              <button
                type="button"
                className="personnel-holiday-save-btn"
                onClick={
                  handleSave
                }
                disabled={
                  busy
                }
              >
                <LuSave />

                {saving
                  ? t(
                      "public_holidays.form.working"
                    )
                  : t(
                      "public_holidays.form.save"
                    )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}